import { Component } from 'react';
import {
  ActivityRun,
  ActivityRunState,
  ProcessRun,
  ProcessRunState,
  TriggerSourceType,
} from '@robocorp/robocloud-orchestrator-interface';
import { ProcessRun as ProcessRunIndexed } from '@robocorp/robocloud-search-iternal-sdk';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
// eslint-disable-next-line import/no-unresolved
import type { OpenEvent } from 'ws';
import { AppState } from '~/store';
import {
  getWorkItemSummaryByProcessRunQueryKey,
  getProcessRunQueryKey,
  getWorkItemSummaryQueryKey,
  getWorkItemSummaryByProcessRunsNoQueryKey,
  indexedProcessRunsQueryUpdater,
  getWorkItemsQueryKey,
  getStepRunQueryKey,
  getStepRunsQueryKey,
} from '~/queries';
import { ActivityRunEvent, RuntimeStateUpdate } from '~/types/resources';
import { withSearchSyncDelay } from '~/queries/utils';
import {
  getRuntimeQueryKey,
  getRuntimesLastActivityRunsQueryKey,
  getWorkersQueryKey,
} from '~/queries/workers/useWorkersQuery';
import { getWorkspaceWebsocketUrl } from '~/utils/api/helper';
import queryClient from '../../queryClient';
import BaseSocket from './BaseSocket';
import { webSocketActions, webSocketMessageTypes } from '../../constants';
import { activityRunActions, processActions, processScheduleActions } from '../../actions';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = WithTranslation & StateProps & DispatchProps;

/**
 * Update step run data from ws messages
 * - doesn't update data if user hasn't fetched the list previously (not on process run page)
 */
const setStepRunsQueryData = (activityRun: ActivityRun) => {
  const activityRunsQueryData: ActivityRun[] | undefined = queryClient.getQueryData(
    getStepRunsQueryKey(activityRun.processRunId),
  );

  // Don't update query cache if previous data doesn't already exists (user not on process run page)
  if (!activityRunsQueryData) {
    return;
  }

  const activityRuns = [...activityRunsQueryData];
  const activityRunIndex = activityRuns.findIndex((run) => run.id === activityRun.id);

  if (activityRunIndex >= 0) {
    activityRuns[activityRunIndex] = { ...activityRuns[activityRunIndex], ...activityRun };
  } else {
    activityRuns.push(activityRun);
  }

  queryClient.setQueryData(getStepRunsQueryKey(activityRun.processRunId), [...activityRuns]);
};

class WorkspaceSocket extends Component<Props> {
  private timeouts: { id: string; timeout: number }[] = [];

  private transientDataQueue: RuntimeStateUpdate[] = [];

  private activityEventQueue: ActivityRunEvent[] = [];

  // These are pretty hacky, but good enough for now
  isOnProcessesView = (): boolean => window.location.pathname.endsWith('/processes');

  throttle = (id: string, callback: () => void) => {
    const timeoutId = this.timeouts.findIndex((item) => item.id === id);

    if (timeoutId > -1) {
      clearTimeout(this.timeouts[timeoutId].timeout);
      this.timeouts.splice(timeoutId, 1);
    }

    const timeout = window.setTimeout(() => {
      callback();
      this.timeouts = this.timeouts.filter((item) => item.id !== id);
    }, 1000);

    this.timeouts.push({ id, timeout });
  };

  /**
   * Throttle and delay state updates on Step run update message
   */
  onActivityRunUpdate = (activityRun: ActivityRun) => {
    // Update step runs & step run query cache
    setStepRunsQueryData(activityRun);

    this.throttle(activityRun.id, () => {
      if (activityRun.state === ActivityRunState.Complete) {
        queryClient.invalidateQueries(
          getWorkItemsQueryKey({ scope: 'process', processId: activityRun.processId }),
        );
      }

      queryClient.invalidateQueries(getStepRunQueryKey(activityRun.processRunId, activityRun.id));

      queryClient.invalidateQueries(
        getWorkItemSummaryQueryKey({ processId: activityRun.processId }),
      );

      queryClient.invalidateQueries(
        getWorkItemSummaryByProcessRunQueryKey(activityRun.processId, activityRun.processRunId),
      );

      withSearchSyncDelay(
        () =>
          queryClient.invalidateQueries(
            getRuntimesLastActivityRunsQueryKey(activityRun.workspaceId, {}),
          ),
        { delayInMs: 5000 },
      );
    });
  };

  /**
   * Throttle and delay state updates on Process run updates
   */
  onProcessRunUpdate = (processRun: ProcessRun): void => {
    const { getProcessStatus, getProcessSchedules } = this.props;

    // Skip process run updates if no process run ID is present (NEW state process run)
    if (!processRun.id) {
      return;
    }

    withSearchSyncDelay(() => {
      // Update query data with updated process run socket data
      queryClient.setQueriesData(
        ['indexedProcessRuns'],
        indexedProcessRunsQueryUpdater(processRun as unknown as ProcessRunIndexed),
      );

      // Invalidate work item related queries
      queryClient.invalidateQueries(getProcessRunQueryKey(processRun.processId, processRun.id));
      queryClient.invalidateQueries(
        getWorkItemsQueryKey({ scope: 'process', processId: processRun.processId }),
      );
      queryClient.invalidateQueries(
        getWorkItemsQueryKey({
          scope: 'processRun',
          processId: processRun.processId,
          processRunId: processRun.id,
        }),
      );
      queryClient.invalidateQueries(
        getWorkItemSummaryByProcessRunsNoQueryKey(processRun.processId).slice(0, 2),
      );
      queryClient.invalidateQueries(
        getWorkItemSummaryQueryKey({ processId: processRun.processId }),
      );
    });

    const isInitialProcessSchedulesFetch =
      processRun.state === ProcessRunState.InProgress &&
      processRun.triggerSource.type === TriggerSourceType.Schedule &&
      processRun.version === 1;

    /**
     * After scheduled process run has been started check when is next due
     * - using record version here to preventing wasteful additional schedule fetches on similar process ws updates
     */
    if (isInitialProcessSchedulesFetch) {
      getProcessSchedules(processRun.processId);
    }

    if (
      processRun.state === ProcessRunState.Complete ||
      processRun.state === ProcessRunState.Pending
    ) {
      if (this.isOnProcessesView()) {
        getProcessStatus(processRun.processId, false);
      }
    }

    // Throttle query invalidation for queries that should invalidate only if the  run does not exist yet
    this.throttle(processRun.id, () => {
      const existsIndexed = queryClient
        .getQueriesData<{ processRuns: ProcessRun[]; total: number }>(['indexedProcessRuns'])
        .find(([, data]) => {
          return data?.processRuns.find(({ id }) => id === processRun.id);
        });

      if (!existsIndexed) {
        queryClient.invalidateQueries(['indexedProcessRuns']);
      }

      this.timeouts = this.timeouts.filter((item) => item.id !== processRun.id);
    });
  };

  onRuntimeStateUpdate = (data: RuntimeStateUpdate) => {
    const { currentWorkspaceId } = this.props;

    this.transientDataQueue.push(data);

    this.throttle('runtime-state-update', () => {
      if (this.transientDataQueue.length) {
        queryClient.invalidateQueries(getRuntimeQueryKey(data.runtimeId));
        queryClient.invalidateQueries(getWorkersQueryKey(currentWorkspaceId));
      }
      this.transientDataQueue = [];
    });
  };

  onActivityRunEventUpdate = (data: ActivityRunEvent) => {
    const { setActivityRunEvent } = this.props;

    this.activityEventQueue.push(data);

    this.throttle('activity-run-event-update', () => {
      setActivityRunEvent(this.activityEventQueue);
      this.activityEventQueue = [];
    });
  };

  /**
   * These messages are not typed anywhere and are from the very beginning of the websocket implementation
   * To see whats actually moving in the websocket, you need to debug it from the console
   * @param data
   * @param actions
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMessage = (data: any, actions: any) => {
    switch (data.messageType) {
      case webSocketMessageTypes.ProcessRunUpdate:
        this.onProcessRunUpdate(data.payload);
        break;
      case webSocketMessageTypes.ActivityRunUpdate:
        this.onActivityRunUpdate(data.payload);
        break;
      case webSocketMessageTypes.AuthOk:
        actions.setConnectionAsOpen();
        break;
      default:
        break;
    }

    switch (data.action) {
      case webSocketActions.STATE_UPDATE:
        this.onRuntimeStateUpdate(data);
        break;
      case webSocketActions.RUN_EVENT:
        this.onActivityRunEventUpdate(data);
        break;
      default:
        break;
    }
  };

  onOpen = (event: OpenEvent, token: string) => {
    const { currentWorkspaceId } = this.props;

    const message = {
      action: 'auth', // maps to auth route on websocket handler
      token,
      wsId: currentWorkspaceId,
    };

    event.target.send(JSON.stringify(message));
  };

  render() {
    const { t } = this.props;
    const params = {};
    return (
      <BaseSocket
        name="WorkspaceSocket"
        url={getWorkspaceWebsocketUrl()}
        onMessage={this.onMessage}
        connectionErrorMessage={t('websocketDisconnectionInfo')}
        connectionSuccessMessage={t('websocketReconnected')}
        queryParams={params}
        logColor="blue"
        onOpen={this.onOpen}
      />
    );
  }
}

const mapStateToProps = (state: AppState) => {
  return {
    currentWorkspaceId: state.ui.currentWorkspaceId,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setActivityRunEvent: activityRunActions.setActivityRunEvent,
      getProcessStatus: processActions.getProcessStatus,
      getProcessSchedules: processScheduleActions.getProcessSchedulesById,
    },
    dispatch,
  );

const connected = connect(mapStateToProps, mapDispatchToProps)(WorkspaceSocket);
export default withTranslation()(connected);
